import { getSubtleSurfaceBackground } from '@app/core/ui/subtle-surface';
import { blinkerAnimation } from '@app/core/ui/animations';
import { usePostHogCaptureLiveTopics } from '@app/modules/logger/screens/stream/features/live-topics/hooks/usePostHogCaptureLiveTopics';
import AssistantIcon from '@mui/icons-material/Assistant';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, IconButton, Typography } from '@mui/material';

export interface LiveTopicsPanelHeaderProps {
  title: string;
  isDetailView: boolean;
  onBack: () => void;
  onClose: () => void;
}

/**
 * Header for the Live Topics panel: back button (in detail view), icon + title, close button.
 */
export function LiveTopicsPanelHeader({
  title,
  isDetailView,
  onBack,
  onClose,
}: LiveTopicsPanelHeaderProps) {
  const capture = usePostHogCaptureLiveTopics();

  const handleBack = () => {
    capture('detail_back');
    onBack();
  };

  const handleClose = () => {
    capture('panel_closed', { view: isDetailView ? 'detail' : 'list' });
    onClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
        backgroundColor: getSubtleSurfaceBackground,
      }}
    >
      {isDetailView ? (
        <IconButton size="small" onClick={handleBack}>
          <KeyboardBackspaceIcon fontSize="small" />
        </IconButton>
      ) : null}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
        <AssistantIcon
          fontSize="small"
          color="info"
          sx={{
            animation: blinkerAnimation,
          }}
        />
        <Typography variant="subtitle1" fontWeight={500}>
          {title}
        </Typography>
      </Box>
      <IconButton size="small" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
